/**
 * Bot Performance Chart
 *
 * Canvas-based chart showing trading bot profit/loss over time
 * Displays cumulative profit for each bot with color-coded lines
 */

import { useRef, useEffect, useState } from 'react';
import type { TradingBot } from '../../lib/api/cryptocom-api';
import { Icons } from '../Icons';
import { fmt } from '../../utils/formatters';

interface BotPerformanceChartProps {
  bots: TradingBot[];
}

interface ChartDataPoint {
  timestamp: number;
  profit: number;
  botId: string;
}

const BOT_COLORS: Record<string, string> = {
  GRID: '#3b82f6',
  DCA: '#10b981',
  TWAP: '#f59e0b',
  FUNDING_ARBITRAGE: '#8b5cf6'
};

export function BotPerformanceChart({ bots }: BotPerformanceChartProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [hoveredBot, setHoveredBot] = useState<string | null>(null);

  // Generate historical data points for bots
  // In real implementation, this would come from API historical data
  const generateChartData = (): Map<string, ChartDataPoint[]> => {
    const dataByBot = new Map<string, ChartDataPoint[]>();

    bots.forEach(bot => {
      const points: ChartDataPoint[] = [];
      const startTime = bot.createdAt;
      const now = Date.now();
      const daysSinceStart = Math.max(1, Math.floor((now - startTime) / (24 * 60 * 60 * 1000)));

      // Generate daily data points from start to now
      for (let i = 0; i <= Math.min(daysSinceStart, 30); i++) {
        const timestamp = startTime + (i * 24 * 60 * 60 * 1000);
        // Simulate progressive profit accumulation
        const progress = i / daysSinceStart;
        const profit = bot.profit * progress;

        points.push({
          timestamp,
          profit,
          botId: bot.id
        });
      }

      dataByBot.set(bot.id, points);
    });

    return dataByBot;
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Set canvas size
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * window.devicePixelRatio;
    canvas.height = rect.height * window.devicePixelRatio;
    ctx.scale(window.devicePixelRatio, window.devicePixelRatio);

    const width = rect.width;
    const height = rect.height;

    // Clear canvas
    ctx.clearRect(0, 0, width, height);

    if (bots.length === 0) {
      ctx.fillStyle = '#888';
      ctx.font = '14px sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText('Aucune donnée de bots disponible', width / 2, height / 2);
      return;
    }

    const chartData = generateChartData();

    // Find min/max for scaling
    let minProfit = 0;
    let maxProfit = 0;
    let minTime = Infinity;
    let maxTime = -Infinity;

    chartData.forEach(points => {
      points.forEach(point => {
        minProfit = Math.min(minProfit, point.profit);
        maxProfit = Math.max(maxProfit, point.profit);
        minTime = Math.min(minTime, point.timestamp);
        maxTime = Math.max(maxTime, point.timestamp);
      });
    });

    // Add padding to y-axis
    const yPadding = (maxProfit - minProfit) * 0.1;
    minProfit -= yPadding;
    maxProfit += yPadding;

    // Chart margins
    const margin = { top: 20, right: 20, bottom: 40, left: 60 };
    const chartWidth = width - margin.left - margin.right;
    const chartHeight = height - margin.top - margin.bottom;

    // Scale functions
    const scaleX = (timestamp: number) => {
      return margin.left + ((timestamp - minTime) / (maxTime - minTime)) * chartWidth;
    };

    const scaleY = (profit: number) => {
      return margin.top + chartHeight - ((profit - minProfit) / (maxProfit - minProfit)) * chartHeight;
    };

    // Draw grid
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.1)';
    ctx.lineWidth = 1;

    // Horizontal grid lines
    for (let i = 0; i <= 5; i++) {
      const y = margin.top + (chartHeight / 5) * i;
      ctx.beginPath();
      ctx.moveTo(margin.left, y);
      ctx.lineTo(margin.left + chartWidth, y);
      ctx.stroke();
    }

    // Draw zero line
    if (minProfit < 0 && maxProfit > 0) {
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.3)';
      ctx.lineWidth = 2;
      const zeroY = scaleY(0);
      ctx.beginPath();
      ctx.moveTo(margin.left, zeroY);
      ctx.lineTo(margin.left + chartWidth, zeroY);
      ctx.stroke();
    }

    // Draw bot lines
    bots.forEach(bot => {
      const points = chartData.get(bot.id);
      if (!points || points.length === 0) return;

      const color = BOT_COLORS[bot.type] || '#888';
      const isHovered = hoveredBot === bot.id;

      ctx.strokeStyle = color;
      ctx.lineWidth = isHovered ? 3 : 2;
      ctx.globalAlpha = isHovered ? 1 : 0.7;

      ctx.beginPath();
      points.forEach((point, i) => {
        const x = scaleX(point.timestamp);
        const y = scaleY(point.profit);

        if (i === 0) {
          ctx.moveTo(x, y);
        } else {
          ctx.lineTo(x, y);
        }
      });
      ctx.stroke();

      // Draw current value dot
      const lastPoint = points[points.length - 1];
      const x = scaleX(lastPoint.timestamp);
      const y = scaleY(lastPoint.profit);

      ctx.fillStyle = color;
      ctx.globalAlpha = 1;
      ctx.beginPath();
      ctx.arc(x, y, isHovered ? 6 : 4, 0, Math.PI * 2);
      ctx.fill();
    });

    ctx.globalAlpha = 1;

    // Draw Y-axis labels
    ctx.fillStyle = '#888';
    ctx.font = '11px sans-serif';
    ctx.textAlign = 'right';

    for (let i = 0; i <= 5; i++) {
      const profit = minProfit + ((maxProfit - minProfit) / 5) * i;
      const y = margin.top + chartHeight - (chartHeight / 5) * i;
      ctx.fillText(`$${profit.toFixed(0)}`, margin.left - 10, y + 4);
    }

    // Draw X-axis labels
    ctx.textAlign = 'center';
    const numXLabels = 5;
    for (let i = 0; i <= numXLabels; i++) {
      const timestamp = minTime + ((maxTime - minTime) / numXLabels) * i;
      const x = scaleX(timestamp);
      const date = new Date(timestamp);
      const label = `${date.getMonth() + 1}/${date.getDate()}`;
      ctx.fillText(label, x, height - 15);
    }

  }, [bots, hoveredBot]);

  const totalProfit = bots.reduce((sum, bot) => sum + bot.profit, 0);
  const activeBots = bots.filter(b => b.status === 'ACTIVE').length;

  return (
    <div className="card">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <h3 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Icons.TrendingUp size={20} />
          Performance des Bots
        </h3>
        <div style={{ display: 'flex', gap: '16px', fontSize: '14px', color: '#888' }}>
          <span>{activeBots} bots actifs</span>
          <span style={{ color: totalProfit >= 0 ? '#10b981' : '#ef4444', fontWeight: 600 }}>
            {fmt.currency(totalProfit)}
          </span>
        </div>
      </div>

      <canvas
        ref={canvasRef}
        style={{
          width: '100%',
          height: '300px',
          cursor: 'crosshair'
        }}
      />

      {/* Legend */}
      <div style={{
        display: 'flex',
        gap: '16px',
        marginTop: '16px',
        flexWrap: 'wrap',
        fontSize: '13px'
      }}>
        {bots.map(bot => (
          <div
            key={bot.id}
            onMouseEnter={() => setHoveredBot(bot.id)}
            onMouseLeave={() => setHoveredBot(null)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              cursor: 'pointer',
              padding: '4px 8px',
              borderRadius: '4px',
              backgroundColor: hoveredBot === bot.id ? 'rgba(255,255,255,0.05)' : 'transparent',
              transition: 'background-color 0.2s'
            }}
          >
            <div
              style={{
                width: '12px',
                height: '12px',
                borderRadius: '2px',
                backgroundColor: BOT_COLORS[bot.type] || '#888'
              }}
            />
            <span style={{ color: '#ccc' }}>
              {bot.symbol} ({bot.type})
            </span>
            <span style={{
              color: bot.profit >= 0 ? '#10b981' : '#ef4444',
              fontWeight: 500
            }}>
              {fmt.currency(bot.profit)}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
